import { normalizeImageForUpload } from '../../utils/imageUploadCompatibility';
import useRequestOwnership from '../useRequestOwnership';
import { useCallback, useRef, useState } from 'react';
import { deleteAdminGroupChatThreadIcon, uploadAdminGroupChatThreadIcon, setAdminGroupChatThreadPresetIcon } from '../../api/adminGroupChat';

type Options = {
  canManage: boolean; ownerScope: string; settingsOpen: boolean;
  settingsThreadIdRef: { current: string }; selectedThreadIdRef: { current: string };
  loadThreadDetail: (id: string, options: { updateSelected: boolean }) => Promise<{ thread_id?: string; [key: string]: unknown } | null>;
  loadThreads: (options: { silent: boolean }) => Promise<unknown>;
  setSettingsThread: (thread: { thread_id?: string; [key: string]: unknown }) => void; toastSuccess: (message: string) => void; toastError: (message: string) => void;
  setShowIconCropModal: (show: boolean) => void; setIconCropImageSrc: (src: string) => void;
  settingsIconFileInputRef: { current: HTMLInputElement | null };
};

export default function useGroupChatIconEditor(options: Options) {
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [deletingIcon, setDeletingIcon] = useState(false);
  const lock = useRef(false);
  const scope = JSON.stringify([options.ownerScope, options.canManage, options.settingsOpen, options.settingsThreadIdRef.current]);
  const latestScope = useRef(scope); latestScope.current = scope;
  const { begin } = useRequestOwnership(scope);
  const { settingsThreadIdRef, selectedThreadIdRef, loadThreadDetail, loadThreads, setSettingsThread,
    toastSuccess, toastError, setShowIconCropModal, setIconCropImageSrc, settingsIconFileInputRef } = options;
  const changeIcon = useCallback(async (image: Blob | string | null, requestedThreadId?: string) => {
    const id = String(requestedThreadId || settingsThreadIdRef.current || selectedThreadIdRef.current || '').trim();
    if (scope !== latestScope.current || !options.canManage || !options.settingsOpen || !id || lock.current) return false;
    const current = begin('icon');
    if (!current() || (requestedThreadId && requestedThreadId !== settingsThreadIdRef.current)) return false;
    lock.current = true;
    const deleting = image == null;
    const setBusy = deleting ? setDeletingIcon : setUploadingIcon;
    setBusy(true);
    try {
      if (typeof image === 'string') await setAdminGroupChatThreadPresetIcon(id, image);
      else if (image) await uploadAdminGroupChatThreadIcon(id, { icon: image });
      else await deleteAdminGroupChatThreadIcon(id);
      if (!current()) return false;
      const [detail] = await Promise.all([
        loadThreadDetail(id, { updateSelected: selectedThreadIdRef.current === id }),
        loadThreads({ silent: true }),
      ]);
      if (current() && settingsThreadIdRef.current === id) {
        if (detail?.thread_id === id) setSettingsThread(detail);
        setShowIconCropModal(false);
        setIconCropImageSrc('');
        if (settingsIconFileInputRef.current) settingsIconFileInputRef.current.value = '';
      }
      if (current()) toastSuccess(deleting ? 'グループチャットアイコンを削除しました' : 'グループチャットアイコンを更新しました');
      return current();
    } catch (error) {
      if (current()) toastError(error instanceof Error ? error.message : 'グループチャットアイコンを更新できませんでした');
      return false;
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }, [begin, scope, options.canManage, options.settingsOpen, loadThreadDetail, loadThreads, selectedThreadIdRef, setIconCropImageSrc, setSettingsThread,
    setShowIconCropModal, settingsIconFileInputRef, settingsThreadIdRef, toastError, toastSuccess]);
  const handleIconFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0];
    if (scope !== latestScope.current || !selected || lock.current || !options.canManage || !options.settingsOpen) return;
    const current = begin('icon');
    if (!current()) return;
    try {
      const file = await normalizeImageForUpload(selected);
      if (!current()) return;
      const reader = new FileReader();
      await new Promise<void>((resolve, reject) => {
        reader.onload = () => {
          if (current()) {
            setIconCropImageSrc(String(reader.result || ''));
            setShowIconCropModal(true);
          }
          resolve();
        };
        reader.onerror = reader.onabort = () => reject(new Error('画像を読み込めませんでした。'));
        reader.readAsDataURL(file);
      });
    } catch (error) {
      if (current()) toastError(error instanceof Error ? error.message : '画像を準備できませんでした。');
    }
  }, [begin, scope, options.canManage, options.settingsOpen, setIconCropImageSrc, setShowIconCropModal, toastError]);
  const handleSaveCroppedIcon = useCallback((image: Blob, id?: string) => image instanceof Blob ? changeIcon(image, id) : Promise.resolve(false), [changeIcon]);
  const handleSavePresetIcon = useCallback((iconFile: string, id?: string) => changeIcon(iconFile, id), [changeIcon]);
  const handleDeleteIcon = useCallback(() => changeIcon(null), [changeIcon]);
  return { uploadingIcon, deletingIcon, handleSaveCroppedIcon, handleSavePresetIcon, handleDeleteIcon, handleIconFileChange };
}
